import { create } from "zustand";
import { persist } from "zustand/middleware";
import { auth } from '../../../../auth';
import convex from "../../../../convexClient";
import { api } from "../../../../convex/_generated/api";
import { normalizeSharedUsers, buildSharedWithIds } from "../../../../utils/familySharing";
import { withActiveTenant } from "../../../../utils/tenantUtils";
import {
  derivePlanMonths,
  normalizeOpeningBalance,
} from "../utils/financialPlanCalculations";

const debounce = (fn, wait = 1000) => {
  let t = null;
  return (...args) => {
    if (t) clearTimeout(t);
    t = setTimeout(() => { t = null; fn(...args); }, wait);
  };
};

const normalizePlanYear = (value) => {
  if (value === null || value === undefined) return "";
  const trimmed = String(value).trim();
  if (!trimmed) return "";
  const numeric = Number(trimmed);
  return Number.isFinite(numeric) ? String(Math.trunc(numeric)) : trimmed;
};

const normalizeSelectedAccountIds = (value) =>
  Array.isArray(value) ? [...new Set(value.map((id) => String(id || "").trim()).filter(Boolean))] : [];

const deriveOpeningBalanceMode = (payload = {}) => {
  if (payload?.openingBalanceMode) return payload.openingBalanceMode;
  if (payload?.includeAccountBalances) return "accounts";
  if (String(payload?.previousBalance || "").trim() && normalizeOpeningBalance(payload?.previousBalance) !== "0.00") {
    return "manual";
  }
  return "zero";
};

export const useFinancialPlanningStore = create(
  persist(
    (set, get) => {
      const initial = {
        planId: null,
        planName: "",
        planYear: "",
        scope: "personal",
        planScope: "full",
        planHalf: "H1",
        planQuarter: "Q1",
        openingBalanceMode: "zero",
        previousBalance: "",
        includeAccountBalances: false,
        selectedAccountIds: [],
        description: "",
        incomeCategories: [],
        savingCategories: [],
        expenseCategories: [],
        sharedWith: [],
        sharedWithIds: [],
        taskId: null,
        saving: false,
        error: null,
        lastSavedAt: null,
        lastAutoSavedId: null,
      };

      const getMonths = () =>
        derivePlanMonths({
          planScope: get().planScope,
          planHalf: get().planHalf,
          planQuarter: get().planQuarter,
        });

      const buildPayload = () => {
        const state = get();
        const normalizedMode = state.openingBalanceMode || "zero";
        const normalizedSelectedAccountIds =
          normalizedMode === "accounts"
            ? normalizeSelectedAccountIds(state.selectedAccountIds)
            : [];
        const normalizedPreviousBalance =
          normalizedMode === "zero"
            ? "0.00"
            : normalizeOpeningBalance(state.previousBalance, {
                allowEmpty: normalizedMode === "manual",
              });

        return {
          planName: state.planName,
          planYear: normalizePlanYear(state.planYear),
          scope: state.scope,
          planScope: state.planScope,
          planHalf: state.planHalf,
          planQuarter: state.planQuarter,
          openingBalanceMode: normalizedMode,
          previousBalance: normalizedPreviousBalance,
          includeAccountBalances: normalizedMode === "accounts",
          selectedAccountIds: normalizedSelectedAccountIds,
          description: state.description,
          incomeCategories: state.incomeCategories || [],
          savingCategories: state.savingCategories || [],
          expenseCategories: state.expenseCategories || [],
          months: getMonths(),
          ...(state.taskId ? { taskId: state.taskId } : {}),
        };
      };

      const canPersistDraft = () => {
        const { planName, planYear } = buildPayload();
        return Boolean(planName?.trim() && planYear);
      };

      const writeDraftToConvex = async (payload, opts = {}) => {
        if (!auth.currentUser) return null;
        try {
          const draftId = get().lastAutoSavedId;
          const id = await convex.mutation(api.financialPlans.saveDraft, {
            draftId: (!opts.forceCreate && draftId) ? draftId : undefined,
            ...payload,
            ...withActiveTenant(),
          });
          return id;
        } catch (err) {
          console.error("writeDraftToConvex error:", err);
          set({ error: err.message || String(err) });
          return null;
        }
      };

      const debouncedSave = debounce(async () => {
        if (!canPersistDraft()) return;
        set({ saving: true, error: null });
        try {
          const id = await writeDraftToConvex(buildPayload());
          if (id) set({ lastAutoSavedId: id, lastSavedAt: Date.now() });
        } catch (err) {
          set({ error: err.message || String(err) });
        } finally {
          set({ saving: false });
        }
      }, 1500);

      return {
        ...initial,

        initialize: (payload = {}) => {
          const normalizedSharedWith = normalizeSharedUsers(payload?.sharedWith || []);
          const resolvedDraftId =
            payload?.draftId ||
            (payload?.status === "draft" ? payload?.id || payload?._id || null : null);
          const resolvedPlanId = resolvedDraftId
            ? null
            : payload?.planId || payload?.id || payload?._id || null;
          set(() => ({
            ...initial,
            ...Object.fromEntries(Object.entries(payload).filter(([, v]) => v !== undefined)),
            planId: resolvedPlanId,
            planYear: normalizePlanYear(payload?.planYear ?? initial.planYear),
            openingBalanceMode: deriveOpeningBalanceMode(payload),
            previousBalance:
              deriveOpeningBalanceMode(payload) === "zero"
                ? "0.00"
                : normalizeOpeningBalance(payload?.previousBalance, {
                    allowEmpty: deriveOpeningBalanceMode(payload) === "manual",
                  }),
            includeAccountBalances: deriveOpeningBalanceMode(payload) === "accounts",
            selectedAccountIds:
              deriveOpeningBalanceMode(payload) === "accounts"
                ? normalizeSelectedAccountIds(
                    payload?.selectedAccountIds ?? initial.selectedAccountIds
                  )
                : [],
            sharedWith: normalizedSharedWith,
            sharedWithIds: buildSharedWithIds(normalizedSharedWith),
            lastAutoSavedId: resolvedDraftId,
            lastSavedAt: resolvedDraftId
              ? payload?.updatedAt ?? payload?.createdAt ?? null
              : null,
          }));
        },

        reset: () => set({ ...initial }),

        setField: (field, value, { autosave = true } = {}) => {
          const normalizedValue =
            field === "planYear"
              ? normalizePlanYear(value)
              : field === "selectedAccountIds"
              ? normalizeSelectedAccountIds(value)
              : field === "previousBalance"
              ? normalizeOpeningBalance(value)
              : value;
          set({ [field]: normalizedValue });
          if (autosave) debouncedSave();
        },

        setOpeningBalanceMode: (openingBalanceMode, { autosave = true } = {}) => {
          const nextMode = openingBalanceMode || "zero";
          const currentState = get();
          const validSelectedAccountIds = normalizeSelectedAccountIds(currentState.selectedAccountIds);
          let nextPreviousBalance = currentState.previousBalance;

          if (nextMode === "zero") {
            nextPreviousBalance = "0.00";
          } else if (nextMode === "manual" && currentState.openingBalanceMode !== "manual") {
            nextPreviousBalance = "";
          } else if (nextMode === "accounts") {
            nextPreviousBalance = normalizeOpeningBalance(currentState.previousBalance, {
              allowEmpty: false,
            });
          }

          set({
            openingBalanceMode: nextMode,
            includeAccountBalances: nextMode === "accounts",
            selectedAccountIds: nextMode === "accounts" ? validSelectedAccountIds : [],
            previousBalance: nextPreviousBalance,
          });
          if (autosave) debouncedSave();
        },

        setAccountBalanceSelection: (
          {
            includeAccountBalances,
            openingBalanceMode = get().openingBalanceMode,
            selectedAccountIds = get().selectedAccountIds,
            previousBalance = get().previousBalance,
          } = {},
          { autosave = true } = {}
        ) => {
          const normalizedMode =
            typeof includeAccountBalances === "boolean"
              ? includeAccountBalances
                ? "accounts"
                : openingBalanceMode || "zero"
              : openingBalanceMode || "accounts";
          const normalizedInclude = normalizedMode === "accounts";
          const normalizedIds = normalizedInclude
            ? normalizeSelectedAccountIds(selectedAccountIds)
            : [];
          const normalizedPreviousBalance = normalizedInclude
            ? normalizeOpeningBalance(previousBalance, {
                allowEmpty: false,
              })
            : normalizedMode === "zero"
            ? "0.00"
            : normalizeOpeningBalance(previousBalance);

          set({
            openingBalanceMode: normalizedMode,
            includeAccountBalances: normalizedInclude,
            selectedAccountIds: normalizedIds,
            previousBalance: normalizedPreviousBalance,
          });
          if (autosave) debouncedSave();
        },

        setCategories: (type, arr = [], { autosave = true } = {}) => {
          const key = `${type}Categories`;
          if (!["incomeCategories","savingCategories","expenseCategories"].includes(key)) return;
          set({ [key]: arr });
          if (autosave) debouncedSave();
        },

        addCategory: (type, category, { autosave = true } = {}) => {
          const key = `${type}Categories`;
          set((state) => {
            const prev = state[key] || [];
            const exists = prev.some((c) => c.id === category.id);
            return { [key]: exists ? prev.map((c) => (c.id === category.id ? category : c)) : [...prev, category] };
          });
          if (autosave) debouncedSave();
        },

        updateCategory: (type, id, patch, { autosave = true } = {}) => {
          const key = `${type}Categories`;
          set((state) => ({
            [key]: (state[key] || []).map((c) => (c.id === id ? { ...c, ...patch } : c)),
          }));
          if (autosave) debouncedSave();
        },

        removeCategory: (type, id, { autosave = true } = {}) => {
          const key = `${type}Categories`;
          set((state) => ({ [key]: (state[key] || []).filter((c) => c.id !== id) }));
          if (autosave) debouncedSave();
        },

        getMonths: () =>
          derivePlanMonths({
            planScope: get().planScope,
            planHalf: get().planHalf,
            planQuarter: get().planQuarter,
          }),

        computeTotalsByMonth: (type) => {
          const key = `${type}Categories`;
          const months = get().getMonths();
          const arr = get()[key] || [];
          const totals = Object.fromEntries(months.map((m) => [m, 0]));
          arr.forEach((cat) => {
            months.forEach((m) => {
              const v = parseFloat(cat?.amounts?.[m]);
              totals[m] += Number.isFinite(v) ? v : 0;
            });
          });
          return totals;
        },

        resetPlan: () => set({
          ...initial,
        }),

        saveDraftNow: async () => {
          if (!canPersistDraft()) return null;
          set({ saving: true, error: null });
          try {
            const id = await writeDraftToConvex(buildPayload(), { forceCreate: false });
            if (id) set({ lastAutoSavedId: id, lastSavedAt: Date.now() });
            return id;
          } catch (err) {
            set({ error: err.message || String(err) });
            return null;
          } finally {
            set({ saving: false });
          }
        },

        discardDraft: async () => {
          const draftId = get().lastAutoSavedId;
          set({ saving: true, error: null });
          try {
            if (draftId && auth.currentUser) {
              await convex.mutation(api.financialPlans.deleteDraft, { id: draftId });
            }
            set({ ...initial });
            return true;
          } catch (err) {
            set({ error: err.message || String(err) });
            throw err;
          } finally {
            set({ saving: false });
          }
        },

        deletePlan: async (planId) => {
          if (!auth.currentUser) { set({ error: "User not authenticated" }); throw new Error("User not authenticated"); }
          if (!planId) { set({ error: "Invalid plan ID" }); throw new Error("Invalid plan ID"); }
          try {
            set({ saving: true, error: null });
            await convex.mutation(api.financialPlans.deletePlan, { id: planId });
              if (get().planId === planId) {
              set({
                planId: null, planName: "", planYear: "", scope: "personal",
                planScope: "full", planHalf: "H1", planQuarter: "Q1",
                openingBalanceMode: "zero", previousBalance: "0.00", includeAccountBalances: false, selectedAccountIds: [], description: "",
                incomeCategories: [], savingCategories: [], expenseCategories: [],
                sharedWith: [], sharedWithIds: [], lastAutoSavedId: null, lastSavedAt: null,
              });
            }
            set({ saving: false });
            return true;
          } catch (err) {
            set({ error: err.message || String(err), saving: false });
            throw err;
          }
        },

        deleteDraft: async (draftId) => {
          if (!auth.currentUser) { set({ error: "User not authenticated" }); throw new Error("User not authenticated"); }
          if (!draftId) { set({ error: "Invalid draft ID" }); throw new Error("Invalid draft ID"); }
          try {
            set({ saving: true, error: null });
            await convex.mutation(api.financialPlans.deleteDraft, { id: draftId });
            if (get().lastAutoSavedId === draftId) {
              set({
                ...initial,
              });
            } else {
              set({ saving: false });
            }
            return true;
          } catch (err) {
            set({ error: err.message || String(err), saving: false });
            throw err;
          }
        },

        savePlan: async (planId = null) => {
          if (!auth.currentUser) { set({ error: "User not authenticated" }); throw new Error("User not authenticated"); }
          try {
            set({ saving: true, error: null });
            const payload = buildPayload();
            const draftId = get().lastAutoSavedId || undefined;
            let id;
            if (planId) {
              await convex.mutation(api.financialPlans.updatePlan, { id: planId, draftId, ...payload });
              id = planId;
            } else {
              id = await convex.mutation(
                api.financialPlans.createPlan,
                withActiveTenant({ ...payload, draftId })
              );
            }
            set({
              lastAutoSavedId: null,
              lastSavedAt: Date.now(),
              planId: id,
              saving: false,
            });
            return id;
          } catch (err) {
            set({ error: err.message || String(err), saving: false });
            throw err;
          }
        },

        triggerAutoSave: () => debouncedSave(),
      };
    },
    {
      name: "financial-planning-store",
      partialize: (state) => ({
        planId: state.planId, planName: state.planName, planYear: state.planYear,
        scope: state.scope, planScope: state.planScope, planHalf: state.planHalf,
        planQuarter: state.planQuarter, openingBalanceMode: state.openingBalanceMode,
        previousBalance: state.previousBalance,
        description: state.description, incomeCategories: state.incomeCategories,
        savingCategories: state.savingCategories, expenseCategories: state.expenseCategories,
        includeAccountBalances: state.includeAccountBalances,
        selectedAccountIds: state.selectedAccountIds,
        taskId: state.taskId,
        lastAutoSavedId: state.lastAutoSavedId, lastSavedAt: state.lastSavedAt,
      }),
    }
  )
);

export default useFinancialPlanningStore;
